import React, { useEffect } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import SimpleBar from "simplebar-react";
import { CHECKLIST_CATEGORY_OPTIONS } from "api/modules/common.option";
import "modules/Volunteer/MasterVolunteer/css/VolunteerFilterModal.scss";

export interface CRFAssignmentFilterValues {
  category?: string | number | null;
}

interface CRFAssignmentFilterModalProps {
  isOpen: boolean;
  toggle: () => void;
  onApply: (filters: CRFAssignmentFilterValues) => void;
  onClear: () => void;
  existingFilters?: CRFAssignmentFilterValues;
}

const customSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: "#f3f3f3",
    borderColor: state.isFocused ? "#405189" : "transparent",
    borderRadius: "4px",
    fontSize: "12px",
    minHeight: "27px",
    height: "auto",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#405189",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 6px",
    fontSize: "12px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#495057",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#adb5bd",
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: "25px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    minHeight: "25px",
    display: "flex",
    alignItems: "center",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 99999,
  }),
};

const CRFAssignmentFilterModal: React.FC<CRFAssignmentFilterModalProps> = ({
  isOpen,
  toggle,
  onApply,
  onClear,
  existingFilters,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<CRFAssignmentFilterValues>({
    defaultValues: existingFilters ?? {},
  });

  useEffect(() => {
    if (existingFilters) {
      reset(existingFilters);
    } else {
      reset({ category: null });
    }
  }, [existingFilters, reset, isOpen]);

  const onSubmit = (values: CRFAssignmentFilterValues) => {
    onApply({
      category: values.category || null,
    });
    toggle();
  };

  const handleClear = () => {
    onClear();
    reset({ category: null });
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="md"
      centered
      backdrop="static"
      className="volunteer-filter-modal common_modal"
    >
      <div className="modal-header">
        <h5 className="modal-title-custom">Filter CRF Module</h5>
        <p className="modal-subtitle pb-2">
          Apply specific criteria to narrow down your selection
        </p>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={toggle}
        ></button>
      </div>

      <ModalBody className="p-0">
        <SimpleBar className="px-4 pt-3 pb-0" style={{ minHeight: "120px", maxHeight: "200px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="filter-section mb-0">
              <h6 className="section-title">CATEGORY FILTER</h6>
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group mb-0">
                    <label>CATEGORY</label>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={CHECKLIST_CATEGORY_OPTIONS}
                          styles={customSelectStyles}
                          menuPortalTarget={document.body}
                          placeholder="All Categories"
                          isClearable
                          value={
                            CHECKLIST_CATEGORY_OPTIONS.find(
                              (opt) => String(opt.value) === String(field.value)
                            ) || null
                          }
                          onChange={(val: any) =>
                            field.onChange(val ? val.value : null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </SimpleBar>
      </ModalBody>

      <div className="modal-footer-custom">
        <button type="button" className="btn-clear" onClick={handleClear}>
          <i className="ri-refresh-line"></i> Clear Filters
        </button>

        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={toggle}>
            Cancel
          </Button>

          <Button
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            outline
            color="primary"
          >
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            Apply Filters
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CRFAssignmentFilterModal;
